import { RoyaltyModelsEnum } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice/RoyaltyModelsEnum';

import { CorePlan, PaperPlanCorePlan, RateCardCoreRateGridColumnName } from 'src/constants';
import { CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP, CORE_PLAN_MAP, CPP_CORE_PLAN_MAP } from 'src/constants/rateCards';
import { IMetadata } from 'src/types/IMetadata';
import { IFullRateGridRowData, IPlanNameToMetadataObj, IRateCardValues } from 'src/types/IRateCard';
import { hasNonEmptyNumberValue } from 'src/util/RateCards';

const getRegionCodeFromTerritoryCode = (territoryCode: string, metadata: IMetadata) => {
    const regionId =
        Object.values(metadata.territoryMapping).find((territory) => territory.territoryCode === territoryCode)
            ?.regionId ?? 0;

    return metadata!.regionMapping![regionId]?.regionCode ?? '';
};

export const populateFullRateRowData = (
    rateCardValues: IRateCardValues,
    planNameToMetadataObj: IPlanNameToMetadataObj,
    metadata: IMetadata
) => {
    const coreRatesTerritoryMap = new Map(
        rateCardValues.coreRates.map((row) => [row[RateCardCoreRateGridColumnName.Territory], row])
    );

    const newFullRates: IFullRateGridRowData[] = [];

    const FULL_CORE_PLAN_MAP = new Map([
        ...Array.from(CORE_PLAN_MAP.entries()),
        ...Array.from(CPP_CORE_PLAN_MAP.entries())
    ]);

    // populate full rate rows for core plans
    rateCardValues.coreRates.forEach((coreRate) => {
        for (const corePlanName of Array.from(FULL_CORE_PLAN_MAP.keys())) {
            if (coreRate[FULL_CORE_PLAN_MAP.get(corePlanName) ?? ''] == undefined) continue;

            const planMetaObj = planNameToMetadataObj[corePlanName];

            const corePlanFullRateRow: IFullRateGridRowData = {
                territory: coreRate[RateCardCoreRateGridColumnName.Territory] ?? '',
                region: getRegionCodeFromTerritoryCode(
                    coreRate[RateCardCoreRateGridColumnName.Territory] ?? '',
                    metadata
                ),
                rateCardCurrency: coreRate[RateCardCoreRateGridColumnName.RatecardCurrency] ?? '',
                settlementCurrency: coreRate[RateCardCoreRateGridColumnName.SettlementCurrency] ?? '',
                cae: coreRate[RateCardCoreRateGridColumnName.CAE] ?? '',
                displayName: planMetaObj['displayName'].toString() ?? '',
                paperPlanName: corePlanName,
                rateGroup: planMetaObj['rateGroup'].toString() ?? '',
                rateType: RoyaltyModelsEnum.PSM,
                rate: coreRate[FULL_CORE_PLAN_MAP.get(corePlanName) ?? ''] ?? '',
                tier2Rate: '',
                tier3Rate: '',
                serviceRev: ''
            };

            if (corePlanName == CorePlan.HAWKFIRE_HOME_ONLY_MONTHLY) {
                if (
                    coreRate[RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM] !== undefined &&
                    coreRate[RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM] !== ''
                ) {
                    corePlanFullRateRow.tier2Rate = coreRate[RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM] ?? '';
                }

                if (
                    coreRate[RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM] !== undefined &&
                    coreRate[RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM] !== ''
                ) {
                    corePlanFullRateRow.tier3Rate = coreRate[RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM] ?? '';
                }
            }

            if (
                [
                    CorePlan.HAWKFIRE_ALL_DEVICES_90DAY_FREE_TRIAL,
                    CorePlan.HAWKFIRE_KATANA_IND_MONTHLY_90_DAY_FT
                ].includes(corePlanName as CorePlan) &&
                coreRate[FULL_CORE_PLAN_MAP.get(corePlanName) ?? ''] !== undefined
            ) {
                corePlanFullRateRow.rateType = RoyaltyModelsEnum.CPP;
            }

            newFullRates.push(corePlanFullRateRow);
        }
    });

    // populate full rate rows for 1P plans
    rateCardValues.firstPartyRates.forEach((firstPartyRate) => {
        for (const corePlanTerritory of Array.from(coreRatesTerritoryMap.keys())) {
            const corePlanTerritoryTerm = coreRatesTerritoryMap.get(corePlanTerritory);

            const corePlan = firstPartyRate.corePlan;
            const corePlanColumn = CORE_PLAN_COLUMN_TO_CORE_RATES_COLUMN_MAP.get(corePlan);
            const corePlanRate = corePlanTerritoryTerm![corePlanColumn!];

            const firstPartyRateMultiplier = hasNonEmptyNumberValue(firstPartyRate.multiplierValue)
                ? Number(firstPartyRate.multiplierValue)
                : 1;

            const firstPartyDerivedRate = (parseFloat(corePlanRate!) * firstPartyRateMultiplier).toString();

            const firstPartyPlanFullRateRow: IFullRateGridRowData = {
                territory: corePlanTerritory ?? '',
                region: getRegionCodeFromTerritoryCode(corePlanTerritory ?? '', metadata),
                rateCardCurrency: corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.RatecardCurrency] ?? '',
                settlementCurrency: corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.SettlementCurrency] ?? '',
                cae: corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.CAE] ?? '',
                displayName: firstPartyRate.displayName,
                paperPlanName: firstPartyRate.paperPlanName,
                rateGroup: firstPartyRate.rateGroup,
                rateType: firstPartyRate.rateType,
                rate: firstPartyDerivedRate,
                tier2Rate: '',
                tier3Rate: '',
                serviceRev: ''
            };

            if (corePlan == PaperPlanCorePlan.ASDM) {
                if (
                    corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM] !== undefined &&
                    corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM] !== ''
                ) {
                    firstPartyPlanFullRateRow.tier2Rate =
                        (
                            parseFloat(corePlanTerritoryTerm[RateCardCoreRateGridColumnName.ASDMonthlyTier2PSM] ?? '') *
                            firstPartyRateMultiplier
                        ).toString() ?? '';
                }

                if (
                    corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM] !== undefined &&
                    corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM] !== ''
                ) {
                    firstPartyPlanFullRateRow.tier3Rate =
                        (
                            parseFloat(corePlanTerritoryTerm[RateCardCoreRateGridColumnName.ASDMonthlyTier3PSM] ?? '') *
                            firstPartyRateMultiplier
                        ).toString() ?? '';
                }
            }

            newFullRates.push(firstPartyPlanFullRateRow);
        }
    });

    // populate full rate rows for 3P plans
    rateCardValues.thirdPartyData.thirdPartyRates.forEach((thirdPartyRate) => {
        const corePlanTerritoryTerm = coreRatesTerritoryMap.get(thirdPartyRate.territory);

        const thirdPartyPlanFullRateRow: IFullRateGridRowData = {
            territory: thirdPartyRate.territory ?? '',
            region: getRegionCodeFromTerritoryCode(thirdPartyRate.territory ?? '', metadata),
            rateCardCurrency: corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.RatecardCurrency] ?? '',
            settlementCurrency: corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.SettlementCurrency] ?? '',
            cae: corePlanTerritoryTerm?.[RateCardCoreRateGridColumnName.CAE] ?? '',
            displayName: thirdPartyRate.displayName,
            paperPlanName: thirdPartyRate.paperPlanName,
            rateGroup: thirdPartyRate.rateGroup,
            rateType: thirdPartyRate.rateType,
            rate: thirdPartyRate.finalRate,
            tier2Rate: '',
            tier3Rate: '',
            serviceRev: thirdPartyRate.serviceRev
        };

        newFullRates.push(thirdPartyPlanFullRateRow);
    });

    return newFullRates;
};
